import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  modules,
  PERMISSION,
  useBusinessPermissions,
  useFeature,
} from "byzantine";
import User from "byzantine/src/User";
import type {
  WebSection,
  WebSectionId,
  WebSectionLeft,
  WebSectionRight,
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";

import {
  applyWebSectionPositionOverrides,
  convertToWebSection,
  filterVisibleSections,
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/utils";
import type { InstitutionFeatures } from "byzantine/src/types/Institution.types";
import { useCurrentUser } from "../../../contexts/CurrentUserContext";
import { useDashboardHiddenSections } from "../../../contexts/DashboardHiddenSectionsContext";
import type { DashboardCustomizationProps } from "../types";
import useIsMarketingBannersEnabled from "../../MarketingImage/hooks";
import useIsMarketingCardVisible from "../../MarketingCardContainer/hooks";
import useIsChartCardEnabled from "../../ChartCard.hooks";
import useIsServicesEnabled from "../../ServicesCard.hooks";
import useCardLinkedOffersFeature from "../../../CardLinkedOffers/hooks";
import useIsCreditMonitoringEnabled from "../../../savvy_money/hooks/useIsCreditMonitoringEnabled";
import useIsSubscriptionManagementEnabled from "../../../subscriptions/SubscriptionCard/hooks";
import { SECTION_COLUMN_SIZES } from "../constants";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";

const {
  useCreateEditUserDisplayPreferences,
  useIsDashboardCustomizationEnabled,
  applyPositionOverrides,
  SECTION,
  WEB_DASHBOARD_SECTIONS_LEFT,
  WEB_DASHBOARD_SECTIONS_RIGHT,
  enforceVisibleSections,
} = modules.userDisplayPreferences;
const { useExternalAccountsOnDashboardEnabled } = modules.externalAccounts;

/**
 * Will return the default columns sorting or a user defined columns sorting.
 * Each allows for positional overrides via the
 * `olb_dashboard_customization_locked_positions` feature flag.
 */
export const useWebDashboardSortOrder = (): {
  leftOrder: WebSectionLeft[];
  rightOrder: WebSectionRight[];
  order: WebSection[];
  lockedPositions: Partial<Record<WebSectionId, number>>;
} => {
  const { currentUser } = useCurrentUser();
  const {
    isDashboardCustomizationEnabled,
    dashboardCustomizationLockedFields,
  } = useIsDashboardCustomizationEnabled();
  const isDashboardRedesignLayoutEnabled =
    modules.dashboard.useDashboardLayoutRedesignEnabled();
  const displayPreferences = currentUser?.user_display_preferences;
  const lockedPositions =
    dashboardCustomizationLockedFields?.web_home_item_positions;

  return useMemo(() => {
    const savedLeft = displayPreferences?.web_home_order_left;
    const savedRight = displayPreferences?.web_home_order_right;
    const savedOrder = displayPreferences?.web_home_order;

    const leftBase: WebSectionLeft[] =
      isDashboardCustomizationEnabled && savedLeft && savedLeft.length > 0
        ? [...savedLeft]
        : [...WEB_DASHBOARD_SECTIONS_LEFT];
    const rightBase: WebSectionRight[] =
      isDashboardCustomizationEnabled && savedRight && savedRight.length > 0
        ? [...savedRight]
        : [...WEB_DASHBOARD_SECTIONS_RIGHT];
    const orderBase: WebSection[] =
      isDashboardRedesignLayoutEnabled && savedOrder && savedOrder.length > 0
        ? [...savedOrder]
        : [
            ...leftBase.map((section) => convertToWebSection(section)),
            ...rightBase.map((section) => convertToWebSection(section)),
          ];
    const overrides = lockedPositions ?? {};

    return {
      leftOrder: applyPositionOverrides(
        leftBase,
        overrides
      ) as WebSectionLeft[],
      rightOrder: applyPositionOverrides(
        rightBase,
        overrides
      ) as WebSectionRight[],
      order: applyWebSectionPositionOverrides(orderBase, overrides),
      lockedPositions: overrides,
    };
  }, [
    isDashboardCustomizationEnabled,
    isDashboardRedesignLayoutEnabled,
    displayPreferences,
    lockedPositions,
  ]);
};

export const useVisibleWebDashboardSections = ({
  externalAccounts,
  servicesApps,
}: DashboardCustomizationProps) => {
  const { leftOrder, rightOrder, order, lockedPositions } =
    useWebDashboardSortOrder();
  const { featureEnabled } = useFeature();
  const tiles = modules.narmiTiles.useTiles();
  const isTilesWebEnabled = featureEnabled("tiles_web");
  const isDashboardRedesignEnabled = featureEnabled(
    "enable_dashboard_layout_redesign"
  );
  const [canTransferInternally] = useBusinessPermissions([
    PERMISSION.BUSINESS.WRITE.INTRA_INSTITUTION_TRANSFER,
  ]);
  const isInternalTransfersEnabled =
    featureEnabled("internal_transfers") && canTransferInternally;
  const isMarketingBannersEnabled = useIsMarketingBannersEnabled();
  const isMarketingCardVisible = useIsMarketingCardVisible();
  const { hiddenSections } = useDashboardHiddenSections();
  const isMarketingBannerImageVisible =
    isMarketingBannersEnabled &&
    !hiddenSections[SECTION.MARKETING_BANNER_IMAGE];
  const isMarketingCardImageVisible =
    isMarketingBannersEnabled && !hiddenSections[SECTION.MARKETING_CARD_IMAGE];
  const isChartCardEnabled = useIsChartCardEnabled();
  const isCreditMonitoringEnabled = useIsCreditMonitoringEnabled();
  const isServicesEnabled = useIsServicesEnabled(servicesApps);
  const isSubscriptionManagementEnabled = useIsSubscriptionManagementEnabled();
  const isSubscriptionsVisible =
    isSubscriptionManagementEnabled && !hiddenSections[SECTION.SUBSCRIPTIONS];
  const isCardLinkedOffersEnabled = useCardLinkedOffersFeature();
  const isExternalAccountsOnDashboardEnabled =
    useExternalAccountsOnDashboardEnabled() && !isDashboardRedesignEnabled;

  const hasUnverifiedExternalAccounts = useMemo(
    () => externalAccounts.some((account) => !account.verified),
    [externalAccounts]
  );

  const leftColumnSectionVisibility = useMemo<Record<WebSectionLeft, boolean>>(
    () => ({
      [SECTION.BALANCE_CHARTS]: isDashboardRedesignEnabled,
      [SECTION.MARKETING_BANNER_IMAGE]: isMarketingBannerImageVisible,
      [SECTION.ACCOUNTS]: true,
      [SECTION.EXTERNAL_ACCOUNTS]: isExternalAccountsOnDashboardEnabled,
      [SECTION.RECENT_TRANSACTIONS]: true,
      [SECTION.CARD_OFFERS]: isCardLinkedOffersEnabled,
    }),
    [
      isDashboardRedesignEnabled,
      isMarketingBannerImageVisible,
      isExternalAccountsOnDashboardEnabled,
      isCardLinkedOffersEnabled,
    ]
  );

  const rightColumnSectionVisibility = useMemo<
    Record<WebSectionRight, boolean>
  >(
    () => ({
      [SECTION.ACCOUNT_TOTALS]: !isDashboardRedesignEnabled,
      [SECTION.CHART_CARD]: isChartCardEnabled,
      [SECTION.CREDIT_MONITORING]: isCreditMonitoringEnabled,
      [SECTION.VERIFY_EXTERNAL_ACCOUNTS]:
        !isExternalAccountsOnDashboardEnabled &&
        !isDashboardRedesignEnabled &&
        hasUnverifiedExternalAccounts,
      [SECTION.MARKETING_CARD]: isMarketingCardVisible,
      [SECTION.SERVICES]: isServicesEnabled,
      [SECTION.SUBSCRIPTIONS]: isSubscriptionsVisible,
      [SECTION.MARKETING_CARD_IMAGE]: isMarketingCardImageVisible,
      [SECTION.QUICK_TRANSFER]:
        isDashboardRedesignEnabled && isInternalTransfersEnabled,
    }),
    [
      isCardLinkedOffersEnabled,
      isChartCardEnabled,
      isCreditMonitoringEnabled,
      isExternalAccountsOnDashboardEnabled,
      isMarketingCardImageVisible,
      isMarketingCardVisible,
      isServicesEnabled,
      isSubscriptionsVisible,
      hasUnverifiedExternalAccounts,
      isDashboardRedesignEnabled,
      isInternalTransfersEnabled,
    ]
  );

  const sectionVisibility = useMemo<Record<WebSectionId, boolean>>(
    () => ({
      [SECTION.BALANCE_CHARTS]: isDashboardRedesignEnabled,
      [SECTION.MARKETING_BANNER_IMAGE]: isMarketingBannerImageVisible,
      [SECTION.ACCOUNTS]: true,
      [SECTION.EXTERNAL_ACCOUNTS]: isExternalAccountsOnDashboardEnabled,
      [SECTION.RECENT_TRANSACTIONS]: true,
      [SECTION.CARD_OFFERS]: isCardLinkedOffersEnabled,
      [SECTION.QUICK_TRANSFER]:
        isDashboardRedesignEnabled && isInternalTransfersEnabled,
      [SECTION.ACCOUNT_TOTALS]: !isDashboardRedesignEnabled,
      [SECTION.CHART_CARD]: isChartCardEnabled,
      [SECTION.CREDIT_MONITORING]: isCreditMonitoringEnabled,
      [SECTION.VERIFY_EXTERNAL_ACCOUNTS]:
        !isExternalAccountsOnDashboardEnabled &&
        !isDashboardRedesignEnabled &&
        hasUnverifiedExternalAccounts,
      [SECTION.MARKETING_CARD]: isMarketingCardVisible,
      [SECTION.SERVICES]: isServicesEnabled,
      [SECTION.SUBSCRIPTIONS]: isSubscriptionsVisible,
      [SECTION.MARKETING_CARD_IMAGE]: isMarketingCardImageVisible,
      [SECTION.BLANK]: true,
    }),
    [
      isCardLinkedOffersEnabled,
      isChartCardEnabled,
      isCreditMonitoringEnabled,
      isExternalAccountsOnDashboardEnabled,
      isMarketingBannerImageVisible,
      isMarketingCardImageVisible,
      isMarketingCardVisible,
      isServicesEnabled,
      isSubscriptionsVisible,
      hasUnverifiedExternalAccounts,
      isDashboardRedesignEnabled,
      isInternalTransfersEnabled,
    ]
  );

  const visibleLeftSectionIds: WebSectionLeft[] = useMemo(() => {
    const visibleLeftSections = Object.entries(leftColumnSectionVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([section]) => section as WebSectionLeft);
    return enforceVisibleSections(leftOrder, visibleLeftSections);
  }, [leftOrder, leftColumnSectionVisibility]);

  const visibleRightSectionIds: WebSectionRight[] = useMemo(() => {
    const visibleRightSections = Object.entries(rightColumnSectionVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([section]) => section as WebSectionRight);
    return enforceVisibleSections(rightOrder, visibleRightSections);
  }, [rightOrder, rightColumnSectionVisibility]);

  const isPageReloadRequired = useMemo(
    () => !!isChartCardEnabled,
    [isChartCardEnabled]
  );
  const allVisibleSectionIds: WebSectionId[] = useMemo(() => {
    const visibleSectionIds = Object.entries(sectionVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([section]) => section as WebSectionId);
    const tileSectionIds = isTilesWebEnabled
      ? tiles.map((tile) => tile.client_id as WebSectionId)
      : [];
    return [...visibleSectionIds, ...tileSectionIds];
  }, [sectionVisibility, tiles, isTilesWebEnabled]);

  const visibleSections: WebSection[] = useMemo(
    () => filterVisibleSections(order, allVisibleSectionIds),
    [order, allVisibleSectionIds]
  );

  const defaultVisibleSections: WebSection[] = useMemo(() => {
    const defaultOrderBase: WebSection[] = [
      ...WEB_DASHBOARD_SECTIONS_LEFT,
      ...WEB_DASHBOARD_SECTIONS_RIGHT,
    ].map((section) => convertToWebSection(section));

    return filterVisibleSections(
      applyWebSectionPositionOverrides(defaultOrderBase, lockedPositions),
      allVisibleSectionIds
    );
  }, [allVisibleSectionIds, lockedPositions]);

  return {
    isPageReloadRequired,
    visibleLeftSectionIds,
    visibleRightSectionIds,
    visibleSections,
    defaultVisibleSections,
  };
};

// assists with displaying items with async checks
const mergeNewlyVisible = <T extends string>(
  current: T[],
  visibleIds: T[]
): T[] => {
  if (visibleIds.length <= current.length) return current;
  const currentSet = new Set(current);
  const missing = visibleIds.filter((id) => !currentSet.has(id));
  return [...current, ...missing];
};

const useDashboardCustomization = ({
  externalAccounts,
  servicesApps,
}: DashboardCustomizationProps) => {
  const { currentUser, setCurrentUser } = useCurrentUser();
  const { user_display_preferences: displayPreferences } = currentUser || {};
  const userCustomizationExists = useRef(!!displayPreferences);
  const hasUserEdits = useRef(false);
  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] =
    useState<boolean>(false);
  const { createEditUserDisplayPreferences, loading } =
    useCreateEditUserDisplayPreferences();

  const {
    isPageReloadRequired,
    visibleLeftSectionIds,
    visibleRightSectionIds,
    visibleSections,
    defaultVisibleSections,
  } = useVisibleWebDashboardSections({ externalAccounts, servicesApps });

  const [leftOrder, setLeftOrderState] = useState<WebSectionLeft[]>(
    visibleLeftSectionIds
  );
  const [rightOrder, setRightOrderState] = useState<WebSectionRight[]>(
    visibleRightSectionIds
  );
  const [order, setOrderState] = useState<WebSection[]>(visibleSections);

  const { aoiob } = useUserFeatures() as InstitutionFeatures;

  const tiles = modules.narmiTiles.useTiles();
  const tileClientIds = useMemo(
    () => new Set(tiles.map((tile) => tile.client_id)),
    [tiles]
  );

  const getSectionSize = useCallback(
    (sectionId: string) => {
      // Narmi tiles are always 1 column
      if (tileClientIds.has(sectionId)) return 1;

      const visibleServices = servicesApps ?? [];
      const servicesCount = visibleServices.length + (aoiob ? 1 : 0);
      const isTwoColumn = sectionId === "services" && servicesCount > 4;
      return isTwoColumn ? 2 : SECTION_COLUMN_SIZES[sectionId as WebSectionId];
    },
    [aoiob, servicesApps, tileClientIds]
  );

  // Loads async items into available items to drag
  // SUBSCRIPTIONS, SERVICES, and VERIFY_EXTERNAL_ACCOUNTS
  useEffect(() => {
    setRightOrderState((current) =>
      mergeNewlyVisible(current, visibleRightSectionIds)
    );
  }, [visibleRightSectionIds]);

  const setLeftOrder = useCallback((next: WebSectionLeft[]) => {
    hasUserEdits.current = true;
    setLeftOrderState(next);
  }, []);

  const setRightOrder = useCallback((next: WebSectionRight[]) => {
    hasUserEdits.current = true;
    setRightOrderState(next);
  }, []);

  const setOrder = useCallback((next: WebSection[]) => {
    hasUserEdits.current = true;
    setOrderState(next);
  }, []);

  const resetOrder = useCallback(() => {
    hasUserEdits.current = true;
    setOrderState(defaultVisibleSections);
  }, [defaultVisibleSections]);

  const openDialogHandler = () => {
    // Reseed from latest visible sections. The persistent provider means we
    // can't rely on mount-time initialization the way atlas does.
    setOrderState(visibleSections);
    setLeftOrderState(visibleLeftSectionIds);
    setRightOrderState(visibleRightSectionIds);
    hasUserEdits.current = false;
    setIsCustomizeDialogOpen(true);
  };

  const closeDialogHandler = () => {
    setIsCustomizeDialogOpen(false);
    hasUserEdits.current = false;
  };

  const submitCustomizationHandler = (forcePageReload = false) => {
    const payload = {
      web_home_order_left: leftOrder,
      web_home_order_right: rightOrder,
      web_home_order: order,
    };
    createEditUserDisplayPreferences(payload, userCustomizationExists.current, {
      onSuccess() {
        setCurrentUser(
          new User({
            ...currentUser,
            user_display_preferences: {
              mobile_app_home_order:
                displayPreferences?.mobile_app_home_order ?? [],
              web_home_order_left: leftOrder,
              web_home_order_right: rightOrder,
              web_home_order: order,
            },
          })
        );
        userCustomizationExists.current = true;
        hasUserEdits.current = false;
        setIsCustomizeDialogOpen(false);

        if (forcePageReload) {
          window.location.reload();
        }
      },
    });
  };

  return {
    isCustomizeDialogOpen,
    isPageReloadRequired,
    openDialogHandler,
    closeDialogHandler,
    submitCustomizationHandler,
    isCustomizeLoading: loading,
    order,
    leftOrder,
    rightOrder,
    setOrder,
    resetOrder,
    setLeftOrder,
    setRightOrder,
    getSectionSize,
  };
};

export default useDashboardCustomization;
